import React, { useState, useEffect, useCallback } from 'react'
import { Navigate } from 'react-router-dom'
import DashboardLayout from '../../components/DashboardLayout'
import EnrollmentManagementTabs from '../../components/admin/EnrollmentManagementTabs'
import { enrollmentService } from '../../api'
import { useAuth } from '../../context/AuthContext'
import toast from 'react-hot-toast'
import {
  Check, X, Book, Calendar, ShieldCheck, AlertCircle, Filter,
} from 'lucide-react'
import ListPageHeader from '../../components/list/ListPageHeader'
import ListToolbar from '../../components/list/ListToolbar'
import ListPagination from '../../components/list/ListPagination'
import ListEmptyState from '../../components/list/ListEmptyState'
import ListLoadingState from '../../components/list/ListLoadingState'
import DataRow, { DataRowList } from '../../components/list/DataRow'
import StatusPill from '../../components/list/StatusPill'
import { parsePaginated, DEFAULT_LIST_PAGE_SIZE } from '../../utils/listHelpers'
import AppSelect from '../../components/ui/AppSelect'

function AdminEnrollments() {
  const { user } = useAuth()
  const isFinanceOnly = user?.user_type === 'finance' && !user?.is_staff && !user?.is_superuser

  const [enrollments, setEnrollments] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE)
  const [actionLoading, setActionLoading] = useState(null)

  const fetchEnrollments = useCallback(async () => {
    setLoading(true)
    try {
      const params = {
        page,
        page_size: searchTerm ? 100 : pageSize,
      }
      if (filter !== 'all') params.status = filter

      const res = await enrollmentService.getAllEnrollments(params)
      const { results, count } = parsePaginated(res)
      setEnrollments(results)
      setTotalCount(searchTerm ? results.length : count)
    } catch {
      toast.error('Failed to load enrollments.')
    } finally {
      setLoading(false)
    }
  }, [filter, page, pageSize, searchTerm])

  useEffect(() => {
    setPage(1)
  }, [filter, searchTerm])

  useEffect(() => {
    if (isFinanceOnly) return
    fetchEnrollments()
  }, [fetchEnrollments, isFinanceOnly])

  const handleApprove = async (enrollment) => {
    setActionLoading(`approve-${enrollment.id}`)
    try {
      await enrollmentService.approveEnrollment(enrollment.id)
      toast.success('Enrollment approved successfully')
      fetchEnrollments()
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error
      toast.error(detail || 'Failed to approve enrollment.')
    } finally {
      setActionLoading(null)
    }
  }

  const handleReject = async (enrollment) => {
    setActionLoading(`reject-${enrollment.id}`)
    try {
      await enrollmentService.rejectEnrollment(enrollment.id)
      toast.success('Enrollment rejected successfully')
      fetchEnrollments()
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error
      toast.error(detail || 'Failed to reject enrollment.')
    } finally {
      setActionLoading(null)
    }
  }

  if (isFinanceOnly) {
    return <Navigate to="/admin/enrollments/payment-verification" replace />
  }

  const filteredEnrollments = enrollments.filter((e) =>
    e.student_name?.toLowerCase().includes(searchTerm.toLowerCase())
    || e.course_title?.toLowerCase().includes(searchTerm.toLowerCase())
    || e.student_email?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const displayCount = searchTerm ? filteredEnrollments.length : totalCount
  const pagedDisplay = searchTerm
    ? filteredEnrollments.slice((page - 1) * pageSize, page * pageSize)
    : filteredEnrollments

  const statusFilter = (
    <AppSelect value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter by status">
      <option value="all">All applications</option>
      <option value="pending">Pending review</option>
      <option value="approved">Approved</option>
      <option value="rejected">Rejected</option>
    </AppSelect>
  )

  return (
    <DashboardLayout>
      <main className="page-container content-width ux-list-page">
        <ListPageHeader
          kicker="Governance & Admissions"
          kickerIcon={ShieldCheck}
          title="Enrollment Management"
          description="Review student applications, verify eligibility, and manage course seat allocations."
        />

        <EnrollmentManagementTabs active="applications" />

        <ListToolbar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by student, email, or course…"
          filters={(
            <>
              <Filter size={16} style={{ color: 'var(--color-text-muted)' }} />
              {statusFilter}
            </>
          )}
        />

        {loading ? (
          <ListLoadingState label="Syncing admissions data…" />
        ) : pagedDisplay.length === 0 ? (
          <ListEmptyState
            icon={AlertCircle}
            title="No matching applications"
            description="No enrollment applications match your current criteria."
            action={(
              <button type="button" className="ux-btn-sm ux-btn-ghost" onClick={() => { setSearchTerm(''); setFilter('all') }}>
                Clear filters
              </button>
            )}
          />
        ) : (
          <DataRowList>
            {pagedDisplay.map((enrollment, index) => {
              const isPending = enrollment.status === 'pending'
              const awaitingPaymentVerification = Boolean(
                isPending
                && enrollment.requires_payment
                && enrollment.can_admin_approve === false
              )
              const canApprove = isPending && (
                !enrollment.requires_payment || enrollment.can_admin_approve !== false
              )

              return (
                <DataRow
                  key={enrollment.id}
                  index={index}
                  className="ux-enrollment-row"
                  leading={(
                    <div className="ux-avatar">
                      {enrollment.student_name ? enrollment.student_name.charAt(0) : 'S'}
                    </div>
                  )}
                  title={enrollment.student_name || 'Anonymous Student'}
                  subtitle={enrollment.student_email || '—'}
                  meta={(
                    <>
                      <span><Book size={12} /> {enrollment.course_title}</span>
                      {awaitingPaymentVerification && (
                        <span title="Finance must verify the payment receipt before admin approval">
                          <AlertCircle size={12} /> Awaiting finance verification
                        </span>
                      )}
                    </>
                  )}
                  badges={(
                    <StatusPill status={enrollment.status} />
                  )}
                  actions={(
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Calendar size={12} />
                        {new Date(enrollment.enrolled_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                      {isPending ? (
                        <>
                          <button
                            type="button"
                            className="ux-icon-btn danger"
                            onClick={() => handleReject(enrollment)}
                            title="Reject enrollment"
                            aria-label="Reject enrollment"
                            disabled={actionLoading === `reject-${enrollment.id}`}
                          >
                            <X size={16} />
                          </button>
                          {canApprove ? (
                            <button
                              type="button"
                              className="ux-icon-btn success"
                              onClick={() => handleApprove(enrollment)}
                              title="Approve enrollment"
                              aria-label="Approve enrollment"
                              disabled={actionLoading === `approve-${enrollment.id}`}
                            >
                              <Check size={16} />
                            </button>
                          ) : awaitingPaymentVerification ? (
                            <span
                              style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', maxWidth: 140, lineHeight: 1.3 }}
                              title="Payment receipt must be verified by finance before approval"
                            >
                              Awaiting payment verification
                            </span>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  )}
                />
              )
            })}
          </DataRowList>
        )}

        {!loading && pagedDisplay.length > 0 && (
          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={displayCount}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
          />
        )}
      </main>
    </DashboardLayout>
  )
}

export default AdminEnrollments
